import { useDropzone } from 'react-dropzone';
import {
  ComponentProps,
  Dispatch,
  ReactElement,
  SetStateAction,
  useCallback,
  useRef,
  useState,
} from 'react';
import { IconButton } from './IconButton';
import { IconButtonInverted } from './IconButtonInverted';
import {
  ArrowUpTrayIcon,
  FolderOpenIcon,
  PaperClipIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useMutation } from '@apollo/client/react';
import { GQL_MUTATION_CREATE_FILE } from '../../queries';
import axios from 'axios';
import { v4 } from 'uuid';
import { DeepNonNullable } from '../../types/utility';
import { Button } from './Button';
import { CreateFileMutation } from '@sc/graphql';
import { Alert } from './Alert';
import { useBlocker } from '@tanstack/react-router';

export type UploadedFile = {
  uuid: string;
  mimeType: string;
  name: string;
  key: string;
  src: string;
  keyPrefix: string;
  size: number;
};

type Props = Omit<
  ComponentProps<typeof IconButton>,
  'Icon' | 'iconClassName' | 'onClick'
> & {
  keyPrefix: string;
  accept?: string;
  onFilesUpload: (files: UploadedFile[]) => void;
  setDeletedAt?: boolean;
  projectUuid?: string;
  asDropzone?: boolean;
  resolveMimeType?: (mineType: string) => string;
  multiple?: boolean;
  fileLimit?: number;
  /**
   * When set, renders a borderless text button with a folder icon instead of an IconButton.
   */
  label?: string;
  uploading: FileToUploadInputType[];
  setUploading: Dispatch<SetStateAction<FileToUploadInputType[]>>;
  inverted?: boolean;
};

export type FileToUploadInputType = {
  uuid: string;
  progress: number;
  file: File;
};

export const UploadButton = ({
  accept,
  onFilesUpload,
  keyPrefix,
  setDeletedAt,
  projectUuid,
  asDropzone,
  resolveMimeType,
  multiple,
  fileLimit = 10,
  label,
  uploading,
  setUploading,
  inverted,
  ...icon
}: Props): ReactElement => {
  const TriggerIconButton = inverted ? IconButtonInverted : IconButton;
  const fileInput = useRef<HTMLInputElement>(null);

  const [createFile] = useMutation(GQL_MUTATION_CREATE_FILE);

  const [errors, setErrors] = useState<string[]>([]);

  useBlocker({
    enableBeforeUnload: () => !!uploading.length,
    shouldBlockFn: () => {
      if (!uploading.length) return false;

      const shouldLeave = confirm(
        'Uploading files, are you sure you want to leave?'
      );

      return shouldLeave;
    },
  });

  const onCompleted = useCallback(
    async ({
      data,
      filesToUpload,
    }: {
      data: CreateFileMutation;
      filesToUpload: FileToUploadInputType[];
    }) => {
      const responses = [];

      for (const create of data.createFile) {
        const file =
          filesToUpload.find((f) => f.uuid === create.uuid) ??
          (filesToUpload.length === 1 ? filesToUpload[0] : null);

        if (!file) throw new Error('Invalid file in completed callback');

        responses.push(
          await axios.put(create.uploadInstructions.url, file.file, {
            headers: {
              'Content-Type': file.file.type,
              'Content-Disposition': `inline; filename="${file.file.name}"`,
            },

            onUploadProgress: (uploadProgress) =>
              setUploading((up) =>
                up.map((u) => {
                  if (u.uuid === file.uuid) {
                    return {
                      ...u,
                      progress: uploadProgress.progress ?? u.progress,
                    };
                  }
                  return u;
                })
              ),
          })
        );
      }

      if (fileInput.current) {
        fileInput.current.value = '';
      }

      const successfulRequests = responses
        .map((r, idx) => ({
          status: r.status,
          originalFile: filesToUpload[idx],
          src: data.createFile[idx]?.src,
          key: data.createFile[idx]?.uploadInstructions.key,
          keyPrefix: data.createFile[idx]?.uploadInstructions.keyPrefix,
        }))
        .filter(
          (r): r is DeepNonNullable<typeof r> =>
            r.status === 200 && !!r.originalFile && !!r.keyPrefix && !!r.key
        );

      onFilesUpload(
        successfulRequests.map((f) => ({
          uuid: f.originalFile.uuid,
          name: f.originalFile.file.name,
          size: f.originalFile.file.size,
          src: f.src,
          key: f.key,
          keyPrefix: f.keyPrefix,
          mimeType: f.originalFile.file.type,
        }))
      );

      setUploading([]);
    },
    [onFilesUpload, setUploading]
  );

  const upload = useCallback(
    (availableFiles: File[]) => {
      const files = availableFiles.filter(
        (f) => f.size <= 30485760 && (!accept || accept.includes(f.type))
      );

      if (files.length > fileLimit) {
        setErrors([
          fileLimit < 1
            ? 'You have reached the maximum number of files.'
            : `You can only add ${fileLimit} more file${
                fileLimit === 1 ? '' : 's'
              }.`,
        ]);
        return;
      }

      if (files.length === 0) {
        setErrors([
          'Upload files must be under 30mb and an uncompressed type.',
        ]);
        return;
      }

      const filesToUpload: FileToUploadInputType[] = files.map((file) => ({
        uuid: v4(),
        file,
        progress: 0,
      }));

      setUploading(filesToUpload);

      void createFile({
        variables: {
          input: {
            projectUuid,
            files: filesToUpload.map(({ uuid, file }) => ({
              uuid,
              mimeType: resolveMimeType
                ? resolveMimeType(file.type)
                : file.type,
              size: file.size,
              filename: file.name,
              deletedAt: setDeletedAt ? new Date().toISOString() : null,
            })),
            keyPrefix,
          },
        },
        onCompleted: (data) => void onCompleted({ data, filesToUpload }),
      });
    },
    [
      accept,
      createFile,
      fileLimit,
      keyPrefix,
      onCompleted,
      projectUuid,
      resolveMimeType,
      setDeletedAt,
      setUploading,
    ]
  );

  const { getInputProps, isDragActive, getRootProps } = useDropzone({
    onDrop: upload,
    multiple,
    maxFiles: fileLimit,
    onError: (e) => alert(e.message),
    onDropRejected(fileRejections, event) {
      const errors = new Set<string>();

      fileRejections.forEach((e) => {
        const [err] = e.errors;
        if (err?.message && !errors.has(err.message)) errors.add(err.message);
      });

      setErrors([...errors]);
    },
  });

  return (
    <>
      <div {...getRootProps()}>
        {asDropzone ? (
          <div
            data-dragactive={isDragActive ? true : undefined}
            className="p-5 flex flex-col items-center border rounded-px border-grey-t-700 border-dashed bg-background-tertiary data-dragactive:border-grey-100"
          >
            <ArrowUpTrayIcon className="size-10 text-grey-500 mb-4" />
            <span className="text-body-medium font-semibold mb-5">
              Drag and drop files here to upload
            </span>
            <Button
              button={{
                text: 'Add file',
                IconEnd: PlusIcon,
                shrink: true,
                type: 'secondary',
                onClick: () => fileInput.current?.click(),
              }}
            />
          </div>
        ) : label ? (
          <Button
            button={{
              text: label,
              IconEnd: FolderOpenIcon,
              type: 'secondary',
              size: 'medium',
              shrink: true,
              disabled: !!uploading.length || icon.disabled,
              onClick: () => fileInput.current?.click(),
            }}
          />
        ) : (
          <TriggerIconButton
            {...icon}
            disabled={!!uploading.length || icon.disabled}
            Icon={PaperClipIcon}
            onClick={() => fileInput.current?.click()}
          />
        )}
        <input
          {...getInputProps()}
          type="file"
          onChange={(e) => void upload(Array.from(e.target.files ?? []))}
          accept={accept}
          hidden
          multiple={multiple}
          ref={fileInput}
        />
      </div>
      {!!errors.length && (
        <Alert
          className="mt-2"
          size="small"
          type="serious"
          onDismiss={() => setErrors([])}
          message={
            <>
              <span>
                Unable to complete upload request, the following errors occured:
              </span>
              <ul className="space-y-2 ml-5 list-disc mt-2">
                {errors.map((err) => (
                  <li>{err}</li>
                ))}
              </ul>
            </>
          }
        />
      )}
    </>
  );
};
